import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api } from "./api.ts";

interface ApiState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

interface ApiOptions {
  /** Intervalo (ms) de atualizacao em background. Silencioso: nao altera `loading`
   *  nem exibe erro por falha transitoria; pausa quando a aba esta oculta. */
  poll?: number;
}

export function useApi<T>(path: string | null, deps: unknown[] = [], options?: ApiOptions): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [nonce, setNonce] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const silentRef = useRef(false);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const silent = silentRef.current;
    silentRef.current = false;
    if (!silent) setLoading(true);
    setError(null);

    api<T>(path, { signal: controller.signal })
      .then((res) => {
        if (!controller.signal.aborted) setData(res);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // polling silencioso: mantem os dados atuais e tenta no proximo ciclo
        if (silent) return;
        if (err instanceof ApiError) setError(err);
        else setError(new ApiError(0, "unknown", String(err)));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  const pollMs = options?.poll;
  useEffect(() => {
    if (!path || !pollMs) return;
    const refresh = () => {
      if (document.hidden) return;
      silentRef.current = true;
      setNonce((n) => n + 1);
    };
    const id = window.setInterval(refresh, pollMs);
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [path, pollMs]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days} d`;
  return formatDate(iso);
}

export function titleCase(value: string): string {
  const map: Record<string, string> = {
    online: "Online",
    offline: "Offline",
    missing: "Ausente",
    frozen: "Congelado",
    wiped: "Apagado",
    retired: "Aposentado",
    pending: "Pendente",
    running: "Em execucao",
    deferred: "Aguardando endpoint",
    completed: "Concluida",
    failed: "Falhou",
    cancelled: "Cancelada",
    encrypted: "Criptografado",
    "not-encrypted": "Sem criptografia",
    encrypting: "Criptografando",
    unknown: "Desconhecido",
    compliant: "Conforme",
    "non-compliant": "Nao conforme",
    "at-risk": "Em risco",
    active: "Ativo",
    suspended: "Suspenso",
    info: "Info",
    warning: "Aviso",
    critical: "Critico",
    freeze: "Freeze",
    wipe: "Wipe",
    "file-delete": "Exclusao de arquivo",
    message: "Mensagem",
    script: "Script",
    reboot: "Reinicio",
    "self-update": "Atualizacao do agente",
    bios_backup: "Backup de BIOS",
    bios_revert: "Reversao do modo teste",
  };
  return map[value] ?? value;
}
